/**
 * ComplianceCenterPageV2 — proper v2 redesign (G119).
 *
 * Dashboard pattern: KPI tiles + framework coverage matrix +
 * per-framework drill-down cards. v1's 9 sub-tabs collapse to a
 * single dashboard with cards that link to the detailed v1 panels
 * for now (Attestations, Remediation, Reg Changes, Reports, AI
 * Assistant, Notifications, Certificates).
 *
 * Reuses every backend code path from v1 ComplianceCenterPage.
 *
 * v1 stays as fallback via /compliance?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthGovernanceEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import {
  RefreshCw, ShieldCheck, FileCheck, AlertTriangle, ClipboardCheck,
  Bell, Award, Activity, ArrowRight, Sparkles, Layers,
} from 'lucide-react';
import {
  Button, Pill, Card, MetricCard,
} from '../components/v2';
/* Phase B / G314-G321 — V2-native slideouts replace the 7 legacy=1
 * bounces. Each slideout is a self-contained workspace; the shell
 * just orchestrates open/close state. */
import {
  AttestationsSlideout, RemediationSlideout, ControlMappingSlideout,
  CertificatesSlideout, RegulatoryChangesSlideout, ReportsSlideout,
  AIAssistantSlideout,
} from '../components/v2/compliance';

import ComplianceCenterPage from './ComplianceCenterPage';

function ComplianceCenterPageV2Inner({ agentScope }) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  /* G325 — Slideout open state. Each surface is independently
   * toggleable; multiple can be open simultaneously (rare but
   * useful when triaging cross-cutting findings). */
  const [openSlideout, setOpenSlideout] = useState(null);
  /* Restore from URL ?slideout=attestations so emails / dashboards
   * can deep-link straight into a workspace. */
  useEffect(() => {
    const s = searchParams.get('slideout');
    if (s) setOpenSlideout(s);
    /* eslint-disable-next-line */
  }, []);
  const handleOpen = (id) => {
    setOpenSlideout(id);
    setSearchParams({ slideout: id }, { replace: true });
  };
  const handleClose = () => {
    setOpenSlideout(null);
    setSearchParams({}, { replace: true });
  };
  /* G133 — agentScope prop takes precedence over the URL query param.
   * Scoped wrappers (NativeCompliancePage / ExternalCompliancePage) pass
   * agentScope="native"/"external" and we treat that as the effective scope. */
  const scopeParam = agentScope || searchParams.get('scope');

  const [dashboard, setDashboard] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  /* G184 — Track API health distinctly from data shape so we can render a
   * proper error banner when the compliance subsystem is down. Previously,
   * silent .catch(() => null) made the KPIs render '—' / 'danger' tone as
   * if compliance was 0% — operators couldn't tell "no data" from "broken
   * platform". */
  const [dashboardError, setDashboardError] = useState(null);
  const [frameworksError, setFrameworksError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setDashboardError(null);
    setFrameworksError(null);
    let d = null, fw = null;
    try {
      if (typeof api.getComplianceDashboard === 'function') {
        try { d = await api.getComplianceDashboard(scopeParam); }
        catch (e) { setDashboardError(e?.message || 'Failed to load compliance dashboard'); }
      } else {
        setDashboardError('api.getComplianceDashboard is not wired');
      }
      if (typeof api.getComplianceFrameworks === 'function') {
        try { fw = await api.getComplianceFrameworks(); }
        catch (e) { setFrameworksError(e?.message || 'Failed to load frameworks'); }
      } else {
        setFrameworksError('api.getComplianceFrameworks is not wired');
      }
      setDashboard(d?.data || d || null);
      const list = Array.isArray(fw?.data?.items) ? fw.data.items
                : Array.isArray(fw?.items) ? fw.items
                : Array.isArray(fw?.data) ? fw.data
                : Array.isArray(fw) ? fw : [];
      setFrameworks(list);
    } finally {
      setLoading(false);
    }
  }, [scopeParam]);
  useEffect(() => { reload(); }, [reload]);

  /* G184 — KPIs separate the value from a `known` flag so the UI can
   * distinguish "0 attestations due" (green) from "data unavailable"
   * (muted). The previous code coerced null → 0 silently which made
   * "broken backend" look like "everything's compliant". */
  const kpis = useMemo(() => {
    const dashKnown = !!dashboard;
    return {
      overallPct:       dashboard?.overallCompliancePct ?? dashboard?.compliancePct ?? null,
      findingsOpen:     dashKnown ? (dashboard?.openFindings ?? 0) : null,
      attestationsDue:  dashKnown ? (dashboard?.attestationsDue ?? 0) : null,
      controlsCovered:  dashKnown ? (dashboard?.controlsCovered ?? 0) : null,
      frameworksActive: frameworks.length,
      timeToRemediate:  dashboard?.timeToRemediateP50Days ?? null,
      dashKnown,
    };
  }, [dashboard, frameworks]);

  const hasAnyError = !!(dashboardError || frameworksError);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Compliance Center</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Enterprise compliance posture, attestations, and regulatory change management.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* Scope toggle (segmented control) */}
          <div className="flex items-center gap-1 bg-v2-muted rounded-v2-pill p-1">
            {[
              { key: null, label: 'All' },
              { key: 'native', label: 'Native' },
              { key: 'external', label: 'External' },
            ].map(s => {
              const active = scopeParam === s.key || (s.key === null && !scopeParam);
              return (
                <button key={s.key || 'all'}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    if (s.key) next.set('scope', s.key); else next.delete('scope');
                    setSearchParams(next);
                  }}
                  className={'px-3 py-1 rounded-v2-pill text-[11.5px] font-semibold transition-colors duration-v2 ' +
                    (active ? 'bg-v2-surface text-v2-ink shadow-v2-sm' : 'text-v2-ink-muted hover:text-v2-ink')}>
                  {s.label}
                </button>
              );
            })}
          </div>
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
        </div>
      </header>

      {/* G184 — Error banner. Shown when either compliance API call failed.
       * Renders BEFORE the KPI tiles so operators see the failure context
       * rather than reading '—' as a real value. */}
      {hasAnyError && !loading && (
        <div className="mb-5 flex items-start gap-2 rounded-v2-md border border-v2-danger-border bg-v2-danger-soft p-3">
          <AlertTriangle size={14} className="text-v2-danger flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-[12.5px] leading-relaxed">
            <div className="font-semibold text-v2-danger">Compliance subsystem unreachable</div>
            <div className="text-v2-ink-secondary mt-0.5">
              KPI tiles below show <em>data unavailable</em> rather than zero.
              {dashboardError && <div className="font-mono-v2 text-[11px] mt-1">Dashboard: {dashboardError}</div>}
              {frameworksError && <div className="font-mono-v2 text-[11px]">Frameworks: {frameworksError}</div>}
            </div>
          </div>
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Retry</Button>
        </div>
      )}

      {/* KPI tiles
       * G184 — Tone falls back to 'muted' when underlying value is null
       * (i.e. the dashboard call failed). Previously a null overall % was
       * compared against 80 / 60 and rendered as 'danger' — that gave
       * operators a false-red signal during backend outages. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <MetricCard
          label="Compliance %"
          value={kpis.overallPct == null ? '—' : `${kpis.overallPct}%`}
          tone={kpis.overallPct == null ? 'muted'
              : kpis.overallPct >= 80 ? 'success'
              : kpis.overallPct >= 60 ? 'warning'
              : 'danger'} />
        <MetricCard
          label="Findings open"
          value={kpis.findingsOpen == null ? '—' : kpis.findingsOpen}
          tone={kpis.findingsOpen == null ? 'muted'
              : kpis.findingsOpen > 0 ? 'warning'
              : 'success'} />
        <MetricCard
          label="Attestations due"
          value={kpis.attestationsDue == null ? '—' : kpis.attestationsDue}
          tone={kpis.attestationsDue == null ? 'muted'
              : kpis.attestationsDue > 0 ? 'warning'
              : 'success'} />
        <MetricCard
          label="Controls covered"
          value={kpis.controlsCovered == null ? '—' : kpis.controlsCovered}
          tone={kpis.controlsCovered == null ? 'muted' : 'cyan'} />
        <MetricCard
          label="Frameworks active"
          value={kpis.frameworksActive}
          tone={frameworksError ? 'muted' : 'trust'} />
        <MetricCard
          label="P50 remediate"
          value={kpis.timeToRemediate == null ? '—' : `${kpis.timeToRemediate}d`}
          tone="muted" />
      </div>

      {/* Drill-down cards (collapses v1's 9 sub-tabs) */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Framework coverage matrix */}
        <Card className="lg:col-span-2">
          <Card.Header title="Framework coverage" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />}
              onClick={() => handleOpen('mapping')}>Open mapping</Button>
          } />
          <Card.Body>
            {frameworks.length === 0 ? (
              <p className="text-v2-ink-muted text-[12.5px]">No frameworks configured for this tenant.</p>
            ) : (
              <ul className="space-y-3">
                {frameworks.slice(0, 6).map(fw => {
                  const pct = fw.compliancePct ?? fw.coveragePct ?? 0;
                  return (
                    <li key={fw.id || fw.name}>
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-[12.5px] font-semibold text-v2-ink">{fw.name || fw.id}</span>
                        <span className="text-[12px] text-v2-ink-secondary tabular-nums">{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-v2-muted rounded-v2-pill overflow-hidden">
                        <div className="h-full" style={{
                          width: `${pct}%`,
                          background: pct >= 80 ? 'var(--v2-success, #047857)'
                                   : pct >= 60 ? 'var(--v2-warning, #92400e)'
                                   : 'var(--v2-danger, #b91c1c)',
                        }} />
                      </div>
                    </li>
                  );
                })}
                {frameworks.length > 6 && (
                  <li className="text-[11.5px] text-v2-ink-muted">+ {frameworks.length - 6} more frameworks</li>
                )}
              </ul>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="Top findings" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />}
              onClick={() => handleOpen('remediation')}>All findings</Button>
          } />
          <Card.Body>
            <div className="text-[28px] font-extrabold tabular-nums text-v2-ink leading-none mb-1">{kpis.findingsOpen}</div>
            <p className="text-[12.5px] text-v2-ink-secondary mb-3">findings open across all frameworks</p>
            {(dashboard?.topFindings || []).slice(0, 3).map((f, i) => (
              <div key={i} className="border-l-2 border-v2-warning pl-2 mb-2">
                <div className="text-[12px] font-semibold text-v2-ink">{f.controlId || f.id}</div>
                <div className="text-[11.5px] text-v2-ink-muted">{f.summary || f.title}</div>
              </div>
            ))}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="Attestations" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />}
              onClick={() => handleOpen('attestations')}>Open attestations</Button>
          } />
          <Card.Body>
            <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
              Track scheduled attestations, executive sign-offs, and quarterly compliance reviews.
            </p>
            <div className="text-[12.5px] text-v2-ink mt-2">
              <strong className="text-v2-warning">{kpis.attestationsDue}</strong> attestation{kpis.attestationsDue === 1 ? '' : 's'} due
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="Regulatory changes" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />}
              onClick={() => handleOpen('changes')}>Open reg changes</Button>
          } />
          <Card.Body>
            <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
              Track upcoming regulatory changes that affect your active controls and policy bindings.
            </p>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="Reports" actions={
            <Button variant="ghost" size="sm" iconLeft={<ArrowRight size={13} />}
              onClick={() => handleOpen('reports')}>Open reports</Button>
          } />
          <Card.Body>
            <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
              Generate compliance evidence packages — SOC 2, HIPAA, PCI, ISO 27001, custom frameworks.
            </p>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="AI Assistant" actions={
            <Button variant="ghost" size="sm" iconLeft={<Sparkles size={13} />}
              onClick={() => handleOpen('assistant')}>Open assistant</Button>
          } />
          <Card.Body>
            <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
              Ask the AI Compliance Assistant about controls, mappings, and remediation strategies.
            </p>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title="Certificates" actions={
            <Button variant="ghost" size="sm" iconLeft={<Award size={13} />}
              onClick={() => handleOpen('certificates')}>Open certificates</Button>
          } />
          <Card.Body>
            <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
              Track active compliance certifications, expiry dates, and audit trail evidence.
            </p>
          </Card.Body>
        </Card>
      </div>

      {/* Phase B / G314-G320 — V2 slideouts. Each is mounted at the
          page level so they render outside the dashboard grid and
          slide in from the right edge. Only one is open at a time
          per the operator UX. */}
      <AttestationsSlideout       isOpen={openSlideout === 'attestations'} onClose={handleClose} addToast={addToast} focusId={searchParams.get('attestation')} />
      <RemediationSlideout        isOpen={openSlideout === 'remediation'}  onClose={handleClose} addToast={addToast} />
      <ControlMappingSlideout     isOpen={openSlideout === 'mapping'}      onClose={handleClose} addToast={addToast} />
      <CertificatesSlideout       isOpen={openSlideout === 'certificates'} onClose={handleClose} addToast={addToast} />
      <RegulatoryChangesSlideout  isOpen={openSlideout === 'changes'}      onClose={handleClose} addToast={addToast} />
      <ReportsSlideout            isOpen={openSlideout === 'reports'}      onClose={handleClose} addToast={addToast} />
      <AIAssistantSlideout        isOpen={openSlideout === 'assistant'}    onClose={handleClose} addToast={addToast} />
    </div>
  );
}

export default function ResolvedComplianceCenterPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthGovernanceEnabled()) return <ComplianceCenterPage {...props} />;
  return <ComplianceCenterPageV2Inner {...props} />;
}

export { ComplianceCenterPageV2Inner };
